import { Draggable } from '@hello-pangea/dnd';
import { Box, Card, Typography, Chip, Avatar, useTheme, Link } from '@mui/material';
import { useRedirect, useTranslate } from 'react-admin';
import { memo } from 'react';
import { formatPhoneForDisplay } from '../../utils/phone';

export type JobApplication = {
  id: number | string;
  candidateName: string;
  email: string;
  phone?: string;
  birthDate?: string;
  city?: string;
  statusId: number;
  behavioralProfile?: string;
  interviewDate?: string;
  sourceId?: number;
  interviewResponsibleId?: number;
  interviewSchedulingResponsibleId?: number;
  supervisorId?: number;
  startedAt?: string;
  companyId?: number;
  observations?: string;
  resumeUrl?: string;
  notes?: string;
  status: string;
  createdAt?: string;
  outcome?: string;
  outcomeAt?: string;
  closureReasonId?: number | null;
  closureReasonName?: string | null;
  closureReasonCategory?: string | null;
  contactedAt?: string;
  interviewScheduledAt?: string;
  completedOnboardingAt?: string;
  hiredAt?: string;
  terminatedAt?: string;
  [key: string]: unknown;
};

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const getWhatsAppLink = (phone: string): string => {
  // Remove any non-digit characters except + for the WhatsApp link
  const cleanPhone = phone.replace(/[^\d+]/g, '');
  // Ensure it starts with + for international format
  const phoneForLink = cleanPhone.startsWith('+') ? cleanPhone : `+${cleanPhone}`;
  return `https://wa.me/${phoneForLink.replace(/\+/g, '')}`;
};

export const JobApplicationCardContent = memo(({
  application,
  isDragging = false,
  onClick,
}: {
  application: JobApplication;
  isDragging?: boolean;
  onClick?: () => void;
}) => {
  const theme = useTheme();
  const translate = useTranslate();

  const handlePhoneClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (application.phone) {
      window.open(getWhatsAppLink(application.phone), '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Box
      onClick={onClick}
      sx={{
        width: '100%',
        cursor: 'pointer',
        transform: isDragging ? 'rotate(2deg) scale(1.02)' : 'none',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Card
        sx={{
          padding: 2,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          boxShadow: isDragging 
            ? theme.shadows[8] 
            : theme.shadows[1],
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: theme.shadows[4],
            borderColor: theme.palette.primary.main,
          },
        }}
      >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {getInitials(application.candidateName)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  variant="subtitle1" 
                  fontWeight={600} 
                  sx={{ 
                    mb: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {application.candidateName}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {application.email}
                </Typography>
              </Box>
            </Box>

            {application.phone && (
              <Box sx={{ mb: 1.5 }}>
                <Link
                  href={getWhatsAppLink(application.phone)}
                  onClick={handlePhoneClick}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    textDecoration: 'none',
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      color: '#25D366', // WhatsApp green
                      textDecoration: 'none',
                    },
                    transition: 'color 0.2s ease',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      backgroundColor: '#25D366',
                      color: 'white',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                    </svg>
                  </Box>
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                  >
                    {formatPhoneForDisplay(application.phone).national}
                  </Typography>
                </Link>
              </Box>
            )}

            {application.createdAt && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  📅 {new Date(application.createdAt).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
              </Box>
            )}

            {application.city && (
              <Box sx={{ mb: 1.5 }}>
                <Chip
                  label={application.city}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.75rem',
                    height: 24,
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.secondary,
                  }}
                />
              </Box>
            )}

            {application.behavioralProfile && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Chip
                  label={translate(`resources.job-application.behavioralProfile.${application.behavioralProfile}`)}
                  size="small"
                  color="primary"
                  variant="filled"
                  sx={{
                    fontSize: '0.7rem',
                    height: 20,
                    fontWeight: 500,
                  }}
                />
              </Box>
            )}
      </Card>
    </Box>
  );
});

export const JobApplicationCard = memo(({ application, index }: { application: JobApplication; index: number }) => {
  const redirect = useRedirect();

  if (!application) return null;

  const handleClick = () => {
    redirect(`/job-application/${application.id}`);
  };

  return (
    <Draggable draggableId={String(application.id)} index={index}>
      {(provided, snapshot) => (
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={provided.draggableProps.style}
          sx={{ width: '100%' }}
        >
          <JobApplicationCardContent
            application={application}
            isDragging={snapshot.isDragging}
            onClick={snapshot.isDragging ? undefined : handleClick}
          />
        </Box>
      )}
    </Draggable>
  );
});
